import React, { useMemo, useState } from 'react';
import { compose } from 'redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Moment from 'moment';
import Interweave from 'interweave';
import { Box, Tab, Tabs } from '@mui/material';
import { useTheme, withStyles } from '@mui/styles';
import { Anchor, Badge, Flex, Tooltip } from '@cowbell/ui/core';
import { Link, useHistory } from 'react-router-dom';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { VirtualTable } from '@devexpress/dx-react-grid-material-ui';

// dev-express table system
import { useQueryParams } from '../../components/providers/QueryParamsProvider';
import { useAPIErrorHandler } from '../../components/hooks/useAPIErrorHandler';
import { useSavedSettings } from '../../components/tables/dev-express/hooks/useSavedSettings';
import { useBackfill } from '../../components/tables/dev-express/hooks/useBackfill';
import { useTableState } from '../../components/tables/dev-express/hooks/useTableState';
import { useQueryHandlers } from '../../components/tables/dev-express/hooks/useQueryHandlers';
import { useUiSettingsAutosave } from '../../components/tables/dev-express/hooks/useUiSettingsAutosave';
import { PrimaryToolbar } from '../../components/tables/dev-express/table-components/toolbar/PrimaryToolbar';
import { SecondaryToolbar } from '../../components/tables/dev-express/table-components/toolbar/SecondaryToolbar';
import { TableSearch } from '../../components/tables/dev-express/table-components/toolbar/TableSearch';
import { ColumnChooser } from '../../components/tables/dev-express/table-components/toolbar/ColumnChooser';
import { CsvDownloadButton } from '../../components/tables/dev-express/table-components/toolbar/CsvDownloadButton';
import { RefreshButton } from '../../components/tables/dev-express/table-components/toolbar/RefreshButton';
import {
  TableStateContext,
  TableStateProvider,
} from '../../components/tables/dev-express/TableStateContext';
import { BaseTable } from '../../components/tables/dev-express/BaseTable';
import {
  rowStyleNames,
  useRowClickGuard,
  useRowStyles,
} from '../../components/tables/dev-express/table-components/rowComponents';
import { RemotePagingPlugin } from '../../components/tables/dev-express/plugins/RemotePagingPlugin';
import type { ColumnConfig } from '../../components/tables/dev-express/types/table.types';

import { ThemedSpotlightIcon } from '../../components/spotlight/SpotlightBanner';
import ToolTipIcon from '../../_assets/svg/Tooltip.svg';
import SimpleTooltip from '../../components/SimpleTooltip';
import { CowbellFactorsMapping } from '../../utility/CowbellFactorsMapping';
import { toUniversalDate, toUniversalUtcDate } from '../../utils/date.utils';
import { useCustomerSpotlightsFetcher } from '../customer-spotlights/spotlight.hooks';
import { getInsights } from './InsightsService';
import { usePersona } from '../../components/hooks/usePersona';
import { FEATURE_FLAG_KEYS } from '../../config/growthbook';
import {
  TableId,
  ActiveTab,
  AffectedProductStatus,
} from './spotlight-details/spotlightDetails.types';
import { getTooltipTitle } from './getTooltipTitle';
import { getStatusConfig } from './utils';

const BACKFILL = {
  size: 25,
  order: 'desc',
  orderBy: 'created',
  before: () => Moment().endOf('day').unix(),
  after: () => Moment().subtract(3, 'years').unix(),
};

const styles = (theme: any) => ({
  tagIcon: {
    width: '1.5rem',
    height: '1.5rem',
    margin: '0 1.5rem',
    background: `url('images/Actionable.svg')`,

    '&:hover': {
      background: `url('images/ActionableBlueLit.svg')`,
    },
  },
  tagIcon1: {
    width: '1.5rem',
    height: '1.5rem',
    margin: '0 1.5rem',
  },
  cowbellIcon: {
    width: '7rem',
    height: '5rem',
  },
  msTagIcon: {
    width: '3.5rem',
    height: '3.5rem',
    margin: '0 0.5rem',
  },
  cfIcon: {
    width: '3.5rem',
    height: '3.5rem',
    margin: 0,
  },
  ssTagIcon: {
    width: '1.5rem',
    height: '1.5rem',
    margin: '0 1.5rem',
  },
  safeGuardTagIcon: {
    width: '2.5rem',
    height: '2.5rem',
    margin: '0 1rem',
  },
  dataSourceContainer: {
    display: 'flex',
    flexDirection: 'row' as const,
    minWidth: 0,
  },
  cellTextEllipsis: {
    display: 'block',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  legendIcon: {
    width: '0.833rem',
    height: '0.833rem',
    margin: '3px 10px 0 0',
  },
  legendText: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.33,
    margin: '0px !important',
  },
  headingMargins: {
    marginTop: '0',
    marginBottom: '0',
  },
  tabsContainer: {
    width: 'fit-content',
    padding: '0',
    margin: '0',
    marginRight: '2rem',
    boxShadow: 'none',
    '& .MuiTabs-root': {
      minHeight: 'auto',
      padding: '0',
      margin: '0',
    },
    '& .MuiTabs-flexContainer': {
      gap: '0.25rem',
    },
  },
  tabContainer: {
    minHeight: '1.75rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    padding: '0.25rem 0.5rem',
    margin: '0',
    color: theme.palette.button.text,
    borderRadius: '0.25rem',
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.button.text2,
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.button.text2,
    },
  },
  mainContainer: {
    padding: '0',
    '& .MuiTablePagination-root': {
      flexShrink: 0,
    },
  },
});

// Helper function to transform severity values for display
export const formatSeverityForDisplay = (severity: string): string => {
  if (!severity) return '';

  const severityStr = severity.toString();
  const lowerSeverity = severityStr.toLowerCase();

  // Handle Information/Info/INFO special case
  if (lowerSeverity === 'information' || lowerSeverity === 'info') {
    return 'Info';
  }

  // Handle underscore separated values like VERY_HIGH -> Very High
  if (severityStr.includes('_')) {
    return severityStr
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // Handle regular cases like Low, Medium, High, etc.
  return (
    severityStr.charAt(0).toUpperCase() + severityStr.slice(1).toLowerCase()
  );
};

const getTableId = (activeTab: string) => {
  if (activeTab === 'spotlight') {
    return TableId.REASONS_RECOMMENDATIONS_SPOTLIGHT;
  }

  if (activeTab === 'insights') {
    return TableId.REASONS_RECOMMENDATIONS_INSIGHTS;
  }

  return TableId.REASONS_RECOMMENDATIONS_INFORMATION;
};

function getReasonsRecommendationsColumnWidths(activeTab: ActiveTab) {
  if (activeTab === ActiveTab.SPOTLIGHT) {
    return {
      primary: '30%',
      affectedProducts: '20%',
      dataSource: '10%',
      mostImpactedCF: '15%',
      importance: '8%',
      created: '8%',
      status: '12%',
    };
  }

  return {
    actionable: '5%',
    primary: '45%',
    dataSource: '10%',
    mostImpactedCF: '15%',
    importance: '8%',
    created: '8%',
    status: '10%',
  };
}

const CF_MAPPINGS = {
  'Company Aggregate': {
    tooltip: CowbellFactorsMapping.companyAggregate.tooltip,
    color: CowbellFactorsMapping.companyAggregate.color,
  },
  'Industry Aggregate': {
    tooltip: CowbellFactorsMapping.industryAggregate.tooltip,
    color: CowbellFactorsMapping.industryAggregate.color,
  },
  'Network Security': {
    tooltip: CowbellFactorsMapping.networkSecurity.tooltip,
    color: CowbellFactorsMapping.networkSecurity.color,
  },
  'Cloud Security': {
    tooltip: CowbellFactorsMapping.cloudSecurity.tooltip,
    color: CowbellFactorsMapping.cloudSecurity.color,
  },
  'Endpoint Security': {
    tooltip: CowbellFactorsMapping.endpointSecurity.tooltip,
    color: CowbellFactorsMapping.endpointSecurity.color,
  },
  'Dark Intelligence': {
    tooltip: CowbellFactorsMapping.darkIntelligence.tooltip,
    color: CowbellFactorsMapping.darkIntelligence.color,
  },
  'Funds Transfer': {
    tooltip: CowbellFactorsMapping.fundsTransfer.tooltip,
    color: CowbellFactorsMapping.fundsTransfer.color,
  },
  'Cyber Extortion': {
    tooltip: CowbellFactorsMapping.cyberExtortion.tooltip,
    color: CowbellFactorsMapping.cyberExtortion.color,
  },
  Compliance: {
    tooltip: CowbellFactorsMapping.compliance.tooltip,
    color: CowbellFactorsMapping.compliance.color,
  },
};

const DATA_SOURCE_CSS = {
  'Cowbell Cyber': 'cowbellIcon',
  'Secureworks Taegis VDR': 'tagIcon1',
  'Microsoft Secure Score': 'msTagIcon',
  'GCP Security Command Center': 'msTagIcon',
  'AWS Security Hub': 'tagIcon1',
  AWS: 'msTagIcon',
  Secureworks: 'tagIcon1',
  'Security Studio': 'ssTagIcon',
  'Qualys Vulnerability Management': 'ssTagIcon',
  'Qualys Policy Compliance': 'ssTagIcon',
  'Qualys Container Security': 'ssTagIcon',
  'Wizer Cyber Training': 'ssTagIcon',
  'Safeguard Cyber': 'safeGuardTagIcon',
  'Google Workspace': 'msTagIcon',
  CLOUDFLARE: 'cfIcon',
  'SOPHOS ENDPOINT': 'msTagIcon',
};

const ActionableCell = ({ row, ctx }: Readonly<{ row: any; ctx: any }>) => {
  const { classes } = ctx;

  if (row?.isSpotlight) {
    return (
      <SimpleTooltip
        title={getTooltipTitle(row)}
        classes={{ tooltip: 'tt-wide' }}
      >
        <ThemedSpotlightIcon
          style={{ margin: '0.5rem 1.5rem 0 1.5rem', fontSize: '2.5rem' }}
        />
      </SimpleTooltip>
    );
  }

  if (row.actionable) {
    return (
      <SimpleTooltip
        title={getTooltipTitle(row)}
        classes={{ tooltip: 'tt-wide' }}
      >
        <div className={classes.tagIcon} />
      </SimpleTooltip>
    );
  }

  return (
    <img alt="" src="images/NonActionable.svg" className={classes.tagIcon1} />
  );
};

const shouldShowReviewInProgress = (persona: any, products: any) => {
  if (!persona.isCowbell || !Array.isArray(products)) {
    return false;
  }

  return products.some((product) =>
    [
      AffectedProductStatus.SUBMIT_FOR_REVIEW,
      AffectedProductStatus.DECLINED,
    ].includes(product.status)
  );
};

const DescriptionCell = ({ row, ctx }: Readonly<{ row: any; ctx: any }>) => {
  if (!row.isSpotlight) {
    return <Interweave content={row?.reasonDescription} />;
  }

  const basePath = ctx?.persona?.basePath;

  if (!basePath || !ctx?.isSpotlightV2Enabled) {
    return (
      <Anchor c="primary" variant="text">
        <Interweave content={row.reasonDescription} />
      </Anchor>
    );
  }

  const showReviewInProgress = shouldShowReviewInProgress(
    ctx.persona,
    row.products
  );

  return (
    <Anchor
      c="primary"
      variant="text"
      component={Link}
      to={`${basePath}/insights/${row.insightId}`}
    >
      <Flex gap="xs" align="center">
        {showReviewInProgress && (
          <div>
            <Tooltip label="Review request in progress">
              <Badge color="yellow" circle size="xs" />
            </Tooltip>
          </div>
        )}
        <Interweave content={row.reasonDescription} />
      </Flex>
    </Anchor>
  );
};

const AFFECTED_PRODUCTS_MAX_VISIBLE = 2;
const AFFECTED_PRODUCTS_MAX_CHARS = 60;

export const AffectedProductsCell = ({ row }: Readonly<{ row: any }>) => {
  if (!Array.isArray(row.products) || row.products.length === 0) {
    return <span>--</span>;
  }

  const names = row.products.map((product: any) => product.name);
  const visibleNames = names.slice(0, AFFECTED_PRODUCTS_MAX_VISIBLE);
  const hasMore = names.length > AFFECTED_PRODUCTS_MAX_VISIBLE;

  let label = visibleNames.join(', ');
  const wasTruncatedByLength = label.length > AFFECTED_PRODUCTS_MAX_CHARS;
  if (wasTruncatedByLength) {
    label = label.slice(0, AFFECTED_PRODUCTS_MAX_CHARS).trimEnd();
  }

  if (!hasMore && !wasTruncatedByLength) {
    return <span>{label}</span>;
  }

  return (
    <span>
      {label}...{' '}
      <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <SimpleTooltip
          title="Click to view more"
          style={{ marginLeft: '0.2rem' }}
        >
          <ToolTipIcon />
        </SimpleTooltip>
      </span>
    </span>
  );
};

const DataSourceCell = ({ row, ctx }: Readonly<{ row: any; ctx: any }>) => {
  const { classes, connectors } = ctx;
  const icon = connectors.icons[row.dataSource]
    ? connectors.icons[row.dataSource]
    : '';
  const css = DATA_SOURCE_CSS[row.dataSource] ?? '';

  return (
    <section className={classes.dataSourceContainer}>
      <img alt="" src={icon} className={classes[css]} />
      <span style={{ display: 'flex', alignItems: 'center' }}>
        {row.dataSource === 'Cowbell Cyber' ? '' : row.dataSource}
      </span>
    </section>
  );
};

const MostImpactedCfCell = ({ row, ctx }: Readonly<{ row: any; ctx: any }>) => {
  const { classes } = ctx;

  if (row.mostImpactedCF === 'All') {
    return <span style={{ color: '#2180e2' }}>All</span>;
  }

  const mapping = CF_MAPPINGS[row.mostImpactedCF];
  const color = mapping ? mapping.color : '';
  const tooltip = mapping ? mapping.tooltip : '';

  return (
    <section className={classes.dataSourceContainer}>
      <div className={classes.legendIcon} style={{ backgroundColor: color }} />
      <p className={classes.legendText} style={{ color }}>
        {row.mostImpactedCF}
      </p>
      <SimpleTooltip title={`${tooltip}`} style={{ marginLeft: '0.2rem' }}>
        <ToolTipIcon />
      </SimpleTooltip>
    </section>
  );
};

const StatusCell = ({ row }: Readonly<{ row: any }>) => {
  if (!row.status) {
    return '--';
  }

  const statusConfig = getStatusConfig(row.status);

  if (!statusConfig) return null;

  return (
    <Badge size="lg" radius="sm" variant="outline" color={statusConfig.color}>
      {statusConfig.label}
    </Badge>
  );
};

// Only Spotlight's data honors spotlight_v2_pg_source; other tabs must
// always render in UTC (CRS-2044).
export const formatInsightsDate = (
  created: Date | string | number | null | undefined,
  isSpotlight: boolean,
  isSpotlightV2PgSource: boolean
): string =>
  isSpotlightV2PgSource && isSpotlight
    ? toUniversalDate(created)
    : toUniversalUtcDate(created);

const useColumnConfig = (
  activeTab: ActiveTab,
  isSpotlightV2PgSource: boolean
): ColumnConfig =>
  useMemo(() => {
    const columnWidths = getReasonsRecommendationsColumnWidths(activeTab);
    const isSpotlightTab = activeTab === ActiveTab.SPOTLIGHT;

    const formatCreated = (row: any) =>
      formatInsightsDate(row.created, isSpotlightTab, isSpotlightV2PgSource);

    const config: ColumnConfig = {};

    if (!isSpotlightTab) {
      config.actionable = {
        name: 'actionable',
        title: 'Actionable',
        sort: false,
        width: columnWidths.actionable,
        getCellValue: (row: any, ctx: any) => (
          <ActionableCell row={row} ctx={ctx} />
        ),
        csv: (row: any) => (row.actionable ? 'true' : 'false'),
      };
    }

    config.reasonDescription = {
      name: 'reasonDescription',
      title: 'Description',
      width: columnWidths.primary,
      getCellValue: (row: any, ctx: any) => (
        <DescriptionCell row={row} ctx={ctx} />
      ),
    };

    if (isSpotlightTab) {
      config.affectedProducts = {
        name: 'affectedProducts',
        title: 'Affected Products',
        sort: false,
        width: columnWidths.affectedProducts,
        getCellValue: (row: any) => <AffectedProductsCell row={row} />,
      };
    }

    config.dataSource = {
      name: 'dataSource',
      title: 'Data Source',
      sort: false,
      width: columnWidths.dataSource,
      getCellValue: (row: any, ctx: any) => (
        <DataSourceCell row={row} ctx={ctx} />
      ),
    };

    config.mostImpactedCF = {
      name: 'mostImpactedCF',
      title: 'Most Impacted CFs',
      sort: !isSpotlightTab,
      width: columnWidths.mostImpactedCF,
      getCellValue: (row: any, ctx: any) => (
        <MostImpactedCfCell row={row} ctx={ctx} />
      ),
    };

    config.importance = {
      name: 'importance',
      title: 'Severity',
      sort: !isSpotlightTab,
      width: columnWidths.importance,
      getCellValue: (row: any) => formatSeverityForDisplay(row.importance),
      csv: (row: any) => formatSeverityForDisplay(row.importance),
    };

    config.created = {
      name: 'created',
      title: 'Date',
      width: columnWidths.created,
      getCellValue: formatCreated,
      csv: formatCreated,
    };

    if (isSpotlightTab) {
      config.status = {
        name: 'status',
        title: 'Status',
        width: columnWidths.status,
        getCellValue: (row: any) => <StatusCell row={row} />,
      };
    }

    return config;
  }, [activeTab, isSpotlightV2PgSource]);

export const SpotlightsTableRow = ({ row, ...props }: any) => {
  const classes = useRowStyles();
  const history = useHistory();
  // @ts-expect-error TS(2339): Property 'context' does not exist on type 'unknow... Remove this comment to see the full error message
  const { context } = React.useContext(TableStateContext);

  const basePath = context?.persona?.basePath;
  const canNavigate = Boolean(
    row.isSpotlight &&
      basePath &&
      context?.isSpotlightV2Enabled &&
      row.insightId
  );

  const clickGuard = useRowClickGuard(
    canNavigate
      ? (e: any) => {
          if ((e.target as HTMLElement).closest('a')) {
            return;
          }
          history.push(`${basePath}/insights/${row.insightId}`);
        }
      : undefined
  );

  return (
    <VirtualTable.Row
      {...clickGuard}
      className={
        canNavigate
          ? classes[rowStyleNames.CLICKABLE]
          : classes[rowStyleNames.DEFAULT]
      }
      data-qa={`TABLE_ROW:${props.tableRow?.rowId}`}
      {...props}
    />
  );
};

const ReasonsRecommendationsTable = ({
  classes,
  accountId,
  recommendationTab,
  tableId,
  activeTab,
}: Readonly<{
  classes: any;
  accountId: any;
  recommendationTab: any;
  tableId: any;
  activeTab: ActiveTab;
}>) => {
  const { query } = useQueryParams();
  const handleAPIError = useAPIErrorHandler();
  const { connectors } = useTheme();
  const persona = usePersona();
  const isSpotlightV2Enabled = useFeatureIsOn(FEATURE_FLAG_KEYS.SPOTLIGHT_V2);
  const isSpotlightV2PgSource = useFeatureIsOn(
    FEATURE_FLAG_KEYS.SPOTLIGHT_V2_PG_SOURCE
  );

  const columnConfig = useColumnConfig(activeTab, isSpotlightV2PgSource);

  const { uiSettings } = useSavedSettings(tableId, columnConfig);
  useBackfill(BACKFILL, uiSettings);

  const { queryHandlers } = useQueryHandlers(query);
  const { state, columns, handlers } = useTableState(columnConfig, {
    uiSettings,
  });

  useUiSettingsAutosave(
    tableId,
    {
      columns,
      columnOrder: state.columnOrder,
      ...query,
    },
    ['before', 'after']
  );

  const customerSpotlightsFetcher = useCustomerSpotlightsFetcher({ accountId });

  const fetchLegacyInsights = (tableQuery: any) =>
    getInsights({ accountId, ...tableQuery, recommendationTab }).then(
      ({ data }: any) => ({
        data: data.content.map((item: any) => ({
          ...item,
          insightId: item.id,
          id: item.findingId || item.reasonName || item.reasonDescription,
        })),
        count: data.totalElements,
      })
    );

  const fetchRows =
    activeTab === ActiveTab.SPOTLIGHT
      ? customerSpotlightsFetcher
      : fetchLegacyInsights;

  const queryClient = useQueryClient();

  const { data, isFetching } = useQuery({
    queryKey: [tableId, accountId, query],
    queryFn: () => fetchRows(query),
    select: (response: any) => ({
      rows: response.data,
      totalCount: response.count,
    }),
    onError: handleAPIError('Unable to retrieve reasons & recommendations.'),
  });

  const onRefetch = () => {
    queryClient.invalidateQueries([tableId]);
  };

  const context = useMemo(
    () => ({ classes, connectors, persona, isSpotlightV2Enabled }),
    [classes, connectors, persona, isSpotlightV2Enabled]
  );

  const mergedState = useMemo(() => ({ ...state, ...query }), [query, state]);
  const mergedHandlers = useMemo(
    () => ({ ...handlers, ...queryHandlers }),
    [handlers, queryHandlers]
  );

  return (
    <section className={classes.mainContainer}>
      <PrimaryToolbar>
        <Box display="flex" justifyContent="flex-end" width="100%">
          <Box width="25rem">
            <TableSearch placeholder="Search" />
          </Box>
        </Box>
      </PrimaryToolbar>

      <TableStateProvider
        state={mergedState}
        handlers={mergedHandlers}
        columns={columns}
        rows={data?.rows ?? []}
        tableId={tableId}
        loading={isFetching}
        context={context}
      >
        <SecondaryToolbar justifyContent="flex-end">
          <ColumnChooser />
          <CsvDownloadButton csvDownloadTitle="Reasons & Recommendations" />
          <RefreshButton onClick={onRefetch} loading={isFetching} />
        </SecondaryToolbar>

        <BaseTable isVirtualized={false} RowComponent={SpotlightsTableRow}>
          <RemotePagingPlugin totalCount={data?.totalCount ?? 0} />
        </BaseTable>
      </TableStateProvider>
    </section>
  );
};

// ui
export const TabbedReasonsRecommendations = compose(
  withStyles(styles, { withTheme: true })
)(({ classes, accountId }: any) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>(ActiveTab.SPOTLIGHT);

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: ActiveTab
  ) => {
    setActiveTab(newValue);
  };

  return (
    <section className={classes.mainContainer}>
      <div style={{ marginBottom: '0.25rem' }}>
        <h1
          className={`table-heading ${classes.headingMargins}`}
          style={{ marginBottom: '1rem' }}
        >
          Reasons & Recommendations
        </h1>
        <div className="flex--spaced" style={{ alignItems: 'center' }}>
          <div className={classes.tabsContainer}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              TabIndicatorProps={{ sx: { display: 'none' } }}
            >
              <Tab
                label="Spotlight"
                value={ActiveTab.SPOTLIGHT}
                className={classes.tabContainer}
                disableRipple
              />
              <Tab
                label="Insights"
                value={ActiveTab.INSIGHTS}
                className={classes.tabContainer}
                disableRipple
              />
              <Tab
                label="Informational"
                value={ActiveTab.INFORMATION}
                className={classes.tabContainer}
                disableRipple
              />
            </Tabs>
          </div>
        </div>
      </div>

      <Box role="tabpanel">
        {activeTab === ActiveTab.SPOTLIGHT && (
          <ReasonsRecommendationsTable
            classes={classes}
            accountId={accountId}
            recommendationTab={ActiveTab.SPOTLIGHT}
            tableId={getTableId(ActiveTab.SPOTLIGHT)}
            activeTab={activeTab}
          />
        )}
        {activeTab === ActiveTab.INSIGHTS && (
          <ReasonsRecommendationsTable
            classes={classes}
            accountId={accountId}
            recommendationTab={ActiveTab.INSIGHTS}
            tableId={getTableId(ActiveTab.INSIGHTS)}
            activeTab={activeTab}
          />
        )}
        {activeTab === ActiveTab.INFORMATION && (
          <ReasonsRecommendationsTable
            classes={classes}
            accountId={accountId}
            recommendationTab={ActiveTab.INFORMATION}
            tableId={getTableId(ActiveTab.INFORMATION)}
            activeTab={activeTab}
          />
        )}
      </Box>
    </section>
  );
});
